import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { cachedSession, fetchSession, type Session } from '../../lib/api.js';
import { profileRoute, type ProfileTab } from '../../router.js';
import { AccountTab } from './account-tab.js';
import { SecurityTab } from './security-tab.js';

const TABS: { key: ProfileTab; label: string }[] = [
  { key: 'account', label: 'Account' },
  { key: 'security', label: 'Security' },
];

export function Profile() {
  const { tab } = profileRoute.useSearch();
  const [session, setSession] = useState<Session | null>(() => cachedSession());

  useEffect(() => {
    let cancelled = false;
    void fetchSession().then((value) => {
      if (!cancelled) setSession(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Nothing on the Security tab would act on the person whose profile is on screen:
  // /api/auth/* resolves the agent's own session, so it is the agent's password and the
  // agent's devices behind every control. The server refuses these routes outright; this
  // just keeps the door from being visible.
  const impersonating = session?.impersonation != null;
  const tabs = TABS.filter((entry) => entry.key !== 'security' || !impersonating);

  const current: ProfileTab = tabs.some((entry) => entry.key === tab)
    ? (tab ?? 'account')
    : 'account';

  return (
    <div className="tab-page">
      <header className="tab-page-head">
        <h1>Profile</h1>
      </header>

      <nav className="tab-strip" aria-label="Profile sections">
        {tabs.map((entry) => (
          <Link
            key={entry.key}
            className="tab-link"
            to="/profile"
            // The default is left out of the URL, so /profile and ?tab=account are the
            // same page rather than two.
            search={entry.key === 'account' ? {} : { tab: entry.key }}
            // Every tab shares the /profile pathname, and an empty `search` is a subset of
            // every other search — so without exact matching the router marks both active
            // at once and underlines the pair.
            activeOptions={{ exact: true, includeSearch: true }}
          >
            {entry.label}
          </Link>
        ))}
      </nav>

      {current === 'account' && <AccountTab session={session} />}
      {current === 'security' && <SecurityTab />}
    </div>
  );
}
